import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AxonautQuotationPicker from "@/components/AxonautQuotationPicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Receipt,
  PlusCircle,
  CheckCircle,
  Package,
  Clock,
} from "@phosphor-icons/react";

/**
 * FinaliserDevisSection — admin-only card on the chantier detail page.
 *
 * • "Finaliser & pousser à Axonaut" → POST /api/chantiers/{id}/finaliser-devis
 *   Builds the definitive quote from actual material posé + real hours.
 * • "Devis complémentaire" → POST /api/chantiers/{id}/devis-complementaire
 *   Pushes ONLY the extras (real − planned).
 *
 * Both actions display the returned Axonaut reference on success and
 * refresh the parent state so the badge switches to "Devis envoyé".
 */
export default function FinaliserDevisSection({ chantier, onChanged }) {
  const { isAdmin } = useAuth();
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null); // 'final' | 'complement'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin || !chantier?.id) return;
    setPreviewLoading(true);
    api
      .get(`/chantiers/${chantier.id}/materiel`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [isAdmin, chantier?.id, chantier?.updated_at]);

  if (!isAdmin) return null;

  const hasQuotationId = !!chantier.axonaut_quotation_id;
  const materialsCount = preview?.items?.length || 0;
  const hoursReal = preview?.hours_real || 0;

  const linkQuotation = async (id) => {
    try {
      await api.patch(`/chantiers/${chantier.id}`, { axonaut_quotation_id: id });
      toast.success(id ? "Devis Axonaut lié au chantier" : "Devis Axonaut délié");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    const url =
      confirmKind === "final"
        ? `/chantiers/${chantier.id}/finaliser-devis`
        : `/chantiers/${chantier.id}/devis-complementaire`;
    setBusy(true);
    try {
      const r = await api.post(url);
      const ref = r?.data?.axonaut_ref || r?.data?.quotation_id || "AXO-OK";
      toast.success(
        confirmKind === "final"
          ? `Devis final créé dans Axonaut — ref ${ref}`
          : `Devis complémentaire créé — ref ${ref}`,
      );
      setConfirmKind(null);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#005CFF]/10 to-transparent border border-[#005CFF]/25 p-3 space-y-3"
      data-testid="finaliser-devis-section"
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#005CFF]/20 flex items-center justify-center shrink-0">
          <Receipt size={18} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">
            Devis Axonaut — depuis le réel
          </div>
          <div className="text-[10px] text-white/50">
            Construit le devis à partir du matériel posé + heures réelles
          </div>
        </div>
        {chantier.axonaut_ref && (
          <div
            className="text-[10px] font-mono px-2 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"
            data-testid="finaliser-devis-ref"
          >
            <CheckCircle size={11} weight="fill" /> {chantier.axonaut_ref}
          </div>
        )}
      </div>

      {/* Inline picker — lets the admin link/change/unlink the Axonaut devis
          without opening the "Modifier chantier" dialog. */}
      <AxonautQuotationPicker
        value={chantier.axonaut_quotation_id}
        onChange={linkQuotation}
        clientId={chantier.client_id}
        testidPrefix="chantier-axo"
      />

      {/* Preview: materials + hours */}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg bg-black/40 p-2 flex items-center gap-2">
          <Package size={14} className="text-emerald-400" weight="duotone" />
          <div className="flex-1">
            <div className="text-white/50 text-[9px] uppercase tracking-widest">
              Matériel posé
            </div>
            <div
              className="font-display font-bold text-emerald-400"
              data-testid="finaliser-devis-materials-count"
            >
              {previewLoading ? "…" : `${materialsCount} réf.`}
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-black/40 p-2 flex items-center gap-2">
          <Clock size={14} className="text-[#7CB2FF]" weight="duotone" />
          <div className="flex-1">
            <div className="text-white/50 text-[9px] uppercase tracking-widest">
              Heures réelles
            </div>
            <div
              className="font-display font-bold text-[#7CB2FF]"
              data-testid="finaliser-devis-hours-real"
            >
              {previewLoading ? "…" : `${hoursReal.toFixed(2)} h`}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={() => setConfirmKind("final")}
          disabled={materialsCount === 0 && hoursReal === 0}
          className="bg-[#005CFF] hover:bg-[#0048CC] text-white font-medium text-xs h-9"
          data-testid="finaliser-devis-btn"
        >
          <Receipt size={14} weight="duotone" className="mr-1" />
          Finaliser
        </Button>
        <Button
          onClick={() => setConfirmKind("complement")}
          disabled={!hasQuotationId}
          variant="ghost"
          className="border border-white/10 hover:border-amber-500/40 text-white font-medium text-xs h-9"
          data-testid="devis-complementaire-btn"
        >
          <PlusCircle size={14} weight="duotone" className="mr-1" />
          Complément
        </Button>
      </div>
      {!hasQuotationId && (
        <div className="text-[10px] text-white/40">
          Le bouton « Complément » est actif dès qu&apos;un devis initial existe
          dans Axonaut.
        </div>
      )}

      <Dialog
        open={!!confirmKind}
        onOpenChange={(o) => !o && setConfirmKind(null)}
      >
        <DialogContent
          className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl"
          data-testid="finaliser-devis-confirm"
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {confirmKind === "final" ? (
                <Receipt size={20} className="text-[#7CB2FF]" weight="duotone" />
              ) : (
                <PlusCircle
                  size={20}
                  className="text-amber-400"
                  weight="duotone"
                />
              )}
              {confirmKind === "final"
                ? "Finaliser le devis Axonaut"
                : "Devis complémentaire"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs text-white/70">
            {confirmKind === "final" ? (
              <>
                <p>
                  BKflow va construire un devis final Axonaut à partir du
                  matériel posé et des heures réellement travaillées, puis le
                  pousser dans ton compte Axonaut.
                </p>
                <ul className="space-y-1 text-[11px]">
                  <li className="flex items-center gap-2">
                    <Package size={12} className="text-emerald-400" />
                    <span className="text-white/60">Matériel :</span>
                    <span className="font-mono">{materialsCount} référence(s)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock size={12} className="text-[#7CB2FF]" />
                    <span className="text-white/60">Main d&apos;œuvre :</span>
                    <span className="font-mono">{hoursReal.toFixed(2)} h</span>
                  </li>
                </ul>
              </>
            ) : (
              <p>
                Seuls les <strong>extras</strong> (posé − prévu, heures réelles
                − heures prévues) seront poussés dans Axonaut. Les lignes
                identiques ou en dessous du devis initial sont ignorées.
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => setConfirmKind(null)}
                disabled={busy}
                className="flex-1 border border-white/10"
                data-testid="finaliser-devis-cancel"
              >
                Annuler
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className={
                  confirmKind === "final"
                    ? "flex-1 bg-[#005CFF] hover:bg-[#0048CC] text-white font-medium"
                    : "flex-1 bg-amber-500 hover:bg-amber-600 text-black font-medium"
                }
                data-testid="finaliser-devis-submit"
              >
                {busy ? "…" : "Pousser à Axonaut"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
